import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/utils/format';
import { inputVariants } from './input';

// const buttonVariants = cva(
//   [
//     'inline-flex items-center justify-center',
//     'border ring-offset-background transition-colors',
//     'disabled:text-color-400 disabled:cursor-not-allowed',
//     'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
//     'hover:cursor-pointer',
//   ],
//   {
//     variants: {
//       variant: {
//         default: 'bg-primary-500 text-white hover:bg-primary-600',
//         secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80 hover:opacity-80',
//         outline:
//           'border border-input bg-background hover:bg-accent hover:text-accent-foreground hover:opacity-80',
//         field: inputVariants({ variant: 'default' }),
//         destructive:
//           'bg-destructive text-destructive-foreground hover:bg-destructive/90 hover:opacity-80',
//         ghost: 'border-0 hover:bg-accent hover:text-accent-foreground hover:opacity-80',
//         link: 'underline-offset-4 hover:underline text-primary-500 border-0 hover:text-primary-600',
//         icon: 'bg-background border hover:opacity-80',
//         action: 'bg-background text-foreground/80 hover:bg-background/20 hover:opacity-80',
//       },
//       size: {
//         xs: 'px-2 py-1 text-xs shadow-xs font-base',
//         sm: 'px-3 py-1 text-sm shadow-xs font-base',
//         md: 'px-4 py-1.5 text-sm shadow-xs font-base',
//         lg: 'px-4 py-2 text-sm shadow-xs font-semibold',
//         xl: 'px-5 py-2.5 text-sm shadow-md font-semibold',
//         icon: 'size-9',
//         'icon-sm': 'size-8',
//         'icon-lg': 'size-10',
//       },
//     },
//     compoundVariants: [
//       {
//         variant: ['default', 'secondary', 'outline', 'destructive'],
//         size: ['xs', 'sm', 'md', 'lg', 'xl'],
//         class: 'rounded-sm',
//       },
//       {
//         variant: 'action',
//         size: ['xs', 'sm', 'md', 'lg', 'xl'],
//         class: 'rounded-sm px-1 py-1',
//       },
//       { variant: 'icon', size: ['xs', 'sm', 'md', 'lg', 'xl'], class: 'rounded-full p-0' },
//       { variant: 'link', size: ['xs', 'sm', 'md', 'lg', 'xl'], class: 'p-0 m-0 shadow-none' },
//     ],
//     defaultVariants: {
//       variant: 'default',
//       size: 'md',
//     },
//   },
// );

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:cursor-pointer',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow hover:bg-primary/90',
        destructive: 'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
        outline:
          'border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        icon: 'bg-background border hover:opacity-80',
        action: 'bg-background text-foreground/80 hover:bg-background/20 hover:opacity-80',
        field: inputVariants({ variant: 'default' }),
      },
      size: {
        xs: 'px-2 py-1 text-xs shadow-xs font-base',
        sm: 'h-8 px-3 text-xs',
        default: 'h-9 px-4 py-2',
        lg: 'h-10 px-8',
        xl: 'px-5 py-2.5 text-sm shadow-md font-semibold',
        icon: 'size-9',
        'icon-sm': 'size-8',
        'icon-lg': 'size-10',
      },
    },
    compoundVariants: [
      {
        variant: 'action',
        size: ['xs', 'sm', 'default', 'lg', 'xl'],
        class: 'rounded-sm px-1 py-1',
      },
      { variant: 'link', size: ['xs', 'sm', 'default', 'lg', 'xl'], class: 'p-0 m-0 shadow-none' },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
